import { syncViewportHeightNow, waitForInputFocusZoomReset } from "./viewportSheet";
import { isMobileViewport } from "./browser";

export interface ScrollToElementByIdOptions {
    behavior?: ScrollBehavior;
    block?: ScrollLogicalPosition;
    focus?: boolean;
}

export {
    getVisualViewportHeight,
    isPageInputFocusZoomActive,
    syncViewportHeightNow,
    syncVisualViewportHeightVar,
    waitForInputFocusZoomReset
} from "./viewportSheet";

export const scrollToElementById = (
    elementId: string,
    { behavior = "smooth", block = "start", focus = true }: ScrollToElementByIdOptions = {}
): boolean => {
    const element = document.getElementById(elementId);
    if (!element) {
        return false;
    }

    element.scrollIntoView({ behavior, block });

    if (focus) {
        element.focus({ preventScroll: true });
    }

    return true;
};

export const getRouteHash = (asPath: string) => asPath.split("#")[1];

/**
 * Zoom is pinned so focusing a field cannot scale the page under a sheet.
 *
 * `interactive-widget` must stay `resizes-content`: `overlays-content` leaves both the layout
 * and visual viewport at full height when the keyboard opens, which pins a sheet's sticky
 * footer under the keyboard and zeroes out every keyboard scroll measurement.
 */
const VIEWPORT_ZOOM_DISABLED_CONTENT =
    "width=device-width,minimum-scale=1,maximum-scale=1,initial-scale=1,user-scalable=no,interactive-widget=resizes-content";

export const getScrollbarWidth = () => {
    if (typeof window === "undefined" || typeof document === "undefined") {
        return 0;
    }

    return Math.max(0, window.innerWidth - document.documentElement.clientWidth);
};

/** Blur the focused control so iOS Safari can exit input-focus zoom before a sheet opens. */
export const blurActiveFormControl = () => {
    if (typeof document === "undefined") {
        return;
    }

    const active = document.activeElement;
    if (active instanceof HTMLElement && active !== document.body && active !== document.documentElement) {
        active.blur();
    }
};

/** Blur + wait for iOS input-focus zoom reset before mounting a mobile full-viewport sheet. */
export const prepareMobileSheetOpen = async (): Promise<void> => {
    if (!isMobileViewport()) {
        return;
    }

    blurActiveFormControl();
    await waitForInputFocusZoomReset();
};

type BodyScrollLockSnapshot = {
    bodyOverflow: string;
    bodyPaddingRight: string;
    bodyPosition: string;
    bodyTop: string;
    bodyLeft: string;
    bodyWidth: string;
    htmlOverflow: string;
    scrollY: number;
};

export const lockBodyScroll = (options?: { freezeOnMobile?: boolean; disableZoom?: boolean }) => {
    const { style: bodyStyle } = document.body;
    const { style: htmlStyle } = document.documentElement;
    const scrollY = window.scrollY;
    const scrollbarWidth = getScrollbarWidth();
    const previous: BodyScrollLockSnapshot = {
        bodyOverflow: bodyStyle.overflow,
        bodyPaddingRight: bodyStyle.paddingRight,
        bodyPosition: bodyStyle.position,
        bodyTop: bodyStyle.top,
        bodyLeft: bodyStyle.left,
        bodyWidth: bodyStyle.width,
        htmlOverflow: htmlStyle.overflow,
        scrollY
    };

    const viewportMeta = document.querySelector('meta[name="viewport"]');
    const previousViewportContent = options?.disableZoom ? viewportMeta?.getAttribute("content") ?? null : null;

    if (options?.disableZoom && viewportMeta) {
        blurActiveFormControl();
        viewportMeta.setAttribute("content", VIEWPORT_ZOOM_DISABLED_CONTENT);
        const syncAfterMetaSwap = () => syncViewportHeightNow();
        requestAnimationFrame(syncAfterMetaSwap);
        [50, 150, 300, 500].forEach((delayMs) => {
            window.setTimeout(syncAfterMetaSwap, delayMs);
        });
    }

    if (options?.freezeOnMobile) {
        bodyStyle.position = "fixed";
        bodyStyle.top = `-${scrollY}px`;
        bodyStyle.left = "0";
        bodyStyle.width = "100%";
        bodyStyle.overflow = "hidden";
        htmlStyle.overflow = "hidden";
    } else {
        bodyStyle.overflow = "hidden";
        htmlStyle.overflow = "hidden";
        if (scrollbarWidth > 0) {
            bodyStyle.paddingRight = `${scrollbarWidth}px`;
        }
    }

    const restoreScrollPosition = () => {
        window.scrollTo(0, previous.scrollY);
        requestAnimationFrame(() => {
            window.scrollTo(0, previous.scrollY);
        });
    };

    return () => {
        bodyStyle.overflow = previous.bodyOverflow;
        bodyStyle.paddingRight = previous.bodyPaddingRight;
        bodyStyle.position = previous.bodyPosition;
        bodyStyle.top = previous.bodyTop;
        bodyStyle.left = previous.bodyLeft;
        bodyStyle.width = previous.bodyWidth;
        htmlStyle.overflow = previous.htmlOverflow;

        if (options?.freezeOnMobile) {
            restoreScrollPosition();
        }

        if (options?.disableZoom && viewportMeta && previousViewportContent) {
            viewportMeta.setAttribute("content", previousViewportContent);
        }

        if (options?.freezeOnMobile) {
            requestAnimationFrame(restoreScrollPosition);
        }
    };
};

export const scrollToElementByIdFromHash = (asPath: string, elementId: string) => {
    if (getRouteHash(asPath) !== elementId) {
        return;
    }

    requestAnimationFrame(() => scrollToElementById(elementId));
};
